import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.ts';
import { TableSkeleton } from '../components/Skeleton.tsx';
import { Icon } from '../components/Icon.tsx';

interface Props {
  groupBy: 'store' | 'kassierer';
}

export function StornoTable({ groupBy }: Props) {
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const { data, isLoading } = useQuery({
    queryKey: ['stornos', groupBy, filters],
    queryFn: () => api.stornos({ filters, groupBy }),
  });

  if (isLoading) return <TableSkeleton rows={10} />;
  const rows = [...(data ?? [])]
    .sort((a, b) => b.storno_quote - a.storno_quote)
    .slice(0, 15);
  const maxQuote = Math.max(...rows.map((r) => r.storno_quote), 0.0001);

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>{groupBy === 'store' ? 'Filiale' : 'Kassierer'}</th>
            <th className="num">Verkäufe</th>
            <th className="num">Stornos</th>
            <th className="num">Quote</th>
            <th className="num">Storno-Summe</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tone =
              r.storno_quote > 0.03
                ? 'text-accent-600 dark:text-accent-500'
                : r.storno_quote > 0.015
                  ? 'text-warning-600 dark:text-warning-500'
                  : 'text-slate-600 dark:text-slate-400';
            const barPct = (r.storno_quote / maxQuote) * 100;
            return (
              <tr
                key={r.label}
                onClick={() =>
                  addFilter(
                    groupBy === 'store' ? 'store_name' : 'kassierer_name',
                    r.label,
                  )
                }
              >
                <td className="truncate max-w-xs">
                  <span className="font-medium">{r.label}</span>
                </td>
                <td className="num">{fmtNumber(r.anz_verkauf)}</td>
                <td className={`num font-semibold ${tone}`}>
                  {fmtNumber(r.anz_storno)}
                </td>
                <td className="num">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div
                        className={`h-full ${
                          r.storno_quote > 0.03
                            ? 'bg-accent-500'
                            : r.storno_quote > 0.015
                              ? 'bg-warning-500'
                              : 'bg-positive-500'
                        }`}
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                    <span className={`font-semibold tabular-nums ${tone}`}>
                      {fmtPercent(r.storno_quote, 2)}
                    </span>
                  </div>
                </td>
                <td className="num font-medium">{fmtCurrency(r.summe_storno)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="py-8 text-center text-sm text-slate-400">
          <Icon name="check" size={24} className="mx-auto mb-2 text-positive-500" />
          Keine Stornos im aktuellen Filter
        </div>
      )}
    </div>
  );
}
